'use client'

import React, { useState, useEffect } from 'react'
import { useGlobalContext } from '../component/context/provider'
import { AxiosGetData } from '../component/context/axiosfunction'
import dayjs from 'dayjs'
import 'dayjs/locale/th'
import relativeTime from 'dayjs/plugin/relativeTime'
import PreorderCard from '../component/Card/PreorderCard'
import Image from 'next/image'
import { imageLoader } from '@/lib/functionloader'

// Swiper
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { getLocalStorage } from '@/lib/localStorage'

dayjs.locale('th')
dayjs.extend(relativeTime)

/* ── Coming Soon Card ── */
const ComingSoonCard = ({ series }) => {

    if (!series) return null
    const book = series.Edata[0]
    const releaseDate = dayjs(series.release_date)
    const daysLeft = releaseDate.diff(dayjs(), 'day')
    const hoursLeft = releaseDate.diff(dayjs(), 'hour')

    return (
        <div className="card p-3 rounded-xl select-none text-primary pointer-events-none">
            {/* Cover */}
            <div className="relative overflow-hidden rounded-lg border border-secondary bg-secondary flex items-center justify-center py-8">
                {/* Badge */}
                <span className="absolute top-0 right-0 bg-gradient-to-br from-gray-800 to-gray-600 text-[#d4af37] px-3 py-1 text-[0.65rem] font-bold rounded-bl-xl shadow-md z-20 tracking-wider">
                    เร็วๆ นี้
                </span>
                {/* Book image */}
                {series.book_image ? (
                    <div className="relative w-[90px] h-[130px] rounded shadow-md border-2 border-white" style={{ transform: 'rotate(-3deg)' }}>
                        <Image
                            src={series.book_image}
                            loader={imageLoader}
                            alt={book?.name ?? ''}
                            fill
                            className="object-cover rounded"
                            sizes="90px"
                        />
                    </div>
                ) : (
                    <div className="w-[90px] h-[130px] bg-gray-300 rounded border-2 border-white shadow-md flex items-center justify-center text-gray-400 text-3xl">
                        📚
                    </div>
                )}
            </div>
            {/* Info */}
            <div className="pt-3 flex flex-col gap-1">
                <div className="text-sm font-bold text-primary line-clamp-2 leading-snug">
                    {book?.name ?? '-'}
                    {series.series_number > 0 && (
                        <span className="ml-1 text-xs text-secondary font-normal">เล่ม {series.series_number}</span>
                    )}
                </div>
                {book?.author?.name && (
                    <div className="text-xs text-secondary">ผู้แต่ง: {book.author.name}</div>
                )}
                {book?.partner?.name && (
                    <div className="text-xs text-secondary">สำนักพิมพ์: {book.partner.name}</div>
                )}
                {/* Countdown */}
                <div className="mt-2 text-center text-xs font-semibold py-1.5 px-2 rounded-lg"
                    style={{
                        background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
                        color: '#a78bfa',
                        letterSpacing: '0.03em'
                    }}
                >
                    {daysLeft > 1
                        ? `วางจำหน่าย ${releaseDate.format('D MMM YYYY')}`
                        : hoursLeft > 0
                            ? `เร็วๆ นี้ (~${hoursLeft} ชม.)`
                            : `เร็วๆ นี้!`
                    }
                </div>
            </div>
        </div>
    )
}

const PreOrderPage = () => {
    const { API, headers, privateKey } = useGlobalContext()

    const [campaigns, setCampaigns] = useState([])
    const [comingSoonBooks, setComingSoonBooks] = useState([])
    const [loading, setLoading] = useState(true)


    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true)
            const [campaignRes, comingSoonRes] = await Promise.all([
                AxiosGetData(API, '/api/preorder/campaigns?status=all&limit=50', headers, privateKey),
                AxiosGetData(API, '/api/coming_soon', headers, privateKey),
            ])
            if (campaignRes?.campaigns) setCampaigns(campaignRes.campaigns)
            if (comingSoonRes) setComingSoonBooks(comingSoonRes)
            setLoading(false)
        }
        fetchAll()
    }, [API, headers, privateKey])

    const openCampaigns = campaigns.filter(c => c.status === 'open')
    const deliveringCampaigns = campaigns.filter(c => c.status === 'delivering')

    if (loading) {
        return (
            <div className="flex justify-center items-center py-32">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500" />
            </div>
        )
    }

    return (
        <div className="w-[100%] pb-12 px-[10px] md:px-[10px] xl:px-[10%] text-primary">
            {/* Banner Section */}
            <div className="w-full bg-gradient-to-r from-[#DB2C6B] to-[#FF5C9F] text-white py-12 px-6 rounded-2xl mb-8 flex flex-col md:flex-row items-center justify-between shadow-md mt-6">
                <div className="mb-4 md:mb-0">
                    <h1 className="text-2xl md:text-3xl font-bold mb-2">Pre-Order นิยาย & การ์ตูน</h1>
                    <p className="text-sm md:text-base opacity-90">สั่งจองก่อนใคร พร้อมโปรโมชั่นพิเศษเฉพาะรอบพรีออเดอร์</p>
                </div>
                <img className="h-[100px] md:h-[130px] hidden md:block" src="https://eve-image.eveebook.com/image.eveebook/mascot/happy.png" alt="mascot" />
            </div>

            {/* 📦 Preorder Zone — open / delivering */}
            <section className="mb-8">
                <div className="flex items-center gap-[10px] text-[20px] xl:text-[24px] font-medium my-[0.5em] border-b pb-2 border-[#858d9738]">
                    <img src="https://eve-image.eveebook.com/image.eveebook/mascot/happy.png" alt="happy_rabbit" className="w-[52px]" />
                    <div className="py-[24px] text-primary">เปิดพรีออเดอร์</div>
                    <div className="text-special">สั่งจองก่อนใคร ได้อ่านก่อนใคร</div>
                </div>

                {openCampaigns.length === 0 && deliveringCampaigns.length === 0 ? (
                    <div className="text-center text-secondary py-16 card rounded-xl">ยังไม่มี Preorder ที่เปิดอยู่</div>
                ) : (
                    <Swiper
                        modules={[Navigation, Pagination]}
                        spaceBetween={24}
                        slidesPerView={2}
                        navigation={true}
                        breakpoints={{
                            640: { slidesPerView: 3 },
                            860: { slidesPerView: 4 },
                            1024: { slidesPerView: 5 },
                        }}
                        className="pb-8"
                    >
                        {[...openCampaigns, ...deliveringCampaigns].map(campaign => (
                            <SwiperSlide key={campaign.id}>
                                <PreorderCard
                                    campaign={campaign}
                                // onClick={() => console.log("Clicked campaign:", campaign.id)}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
            </section>

            {/* 🕐 Coming Soon Zone */}
            <section className="mt-12 mb-8">
                <div className="flex items-center gap-[10px] text-[20px] xl:text-[24px] font-medium my-[0.5em] border-b pb-2 border-[#858d9738]">
                    <img src="https://eve-image.eveebook.com/image.eveebook/mascot/happy.png" alt="happy_rabbit" className="w-[52px]" />
                    <div className="py-[24px] text-primary">Coming Soon</div>
                    <div className="text-special">เตรียมตัวให้พร้อม เร็วๆ นี้</div>
                </div>

                {comingSoonBooks.length === 0 ? (
                    <div className="text-center text-secondary py-12 card rounded-xl">ยังไม่มีหนังสือที่กำลังจะมา</div>
                ) : (
                    <Swiper
                        modules={[Navigation, Pagination]}
                        spaceBetween={20}
                        slidesPerView={2}
                        navigation={true}
                        breakpoints={{
                            640: { slidesPerView: 3 },
                            768: { slidesPerView: 4 },
                            1024: { slidesPerView: 5 },
                            1280: { slidesPerView: 6 },
                        }}
                        className="pb-8"
                    >
                        {comingSoonBooks.map(series => (
                            <SwiperSlide key={series.id}>
                                <ComingSoonCard series={series} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
            </section>
        </div>
    )
}

export default PreOrderPage